// SkipToast.tsx
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

type SkipToastProps = {
  visible: boolean;
  onClose: () => void;
  duration?: number; // optional auto close
};

export const SkipToast = ({
  visible,
  onClose,
  duration = 3500,
}: SkipToastProps) => {
  const [mounted, setMounted] = useState(false);

  // Smooth mount animation
  useEffect(() => {
    if (visible) {
      setMounted(true);

      const timer = setTimeout(() => {
        onClose();
      }, duration);

      return () => clearTimeout(timer);
    } else {
      setMounted(false);
    }
  }, [visible, duration, onClose]);

  if (!visible) return null;

  return createPortal(
    <div
      className={`
    fixed top-6 left-1/2 -translate-x-1/2 z-[99999]
    transition-all duration-300 ease-out
    ${mounted ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"}
  `}
    >
      <div className="relative w-[360px] bg-[#fcfcfd] backdrop-blur-sm rounded-2xl border border-gray-200/60 shadow-lg p-4 flex gap-3">
        {/* Icon */}
        <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-orange-100/70">
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            className="text-orange-500"
          >
            <path
              d="M9 2.25a6.75 6.75 0 1 0 0 13.5A6.75 6.75 0 0 0 9 2.25ZM9 6a.75.75 0 0 1 .75.75v3a.75.75 0 0 1-1.5 0v-3A.75.75 0 0 1 9 6Zm0 6.375a.9.9 0 1 1 0-1.8.9.9 0 0 1 0 1.8Z"
              fill="currentColor"
            />
          </svg>
        </div>

        {/* Content */}
        <div className="flex-1 pr-5">
          <p className="text-sm font-medium text-gray-800">Section skipped</p>
          <p className="text-xs text-gray-500 mt-1 leading-relaxed">
            You can complete this anytime from your profile settings.
          </p>
        </div>

        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X size={15} />
        </button>

        {/* Progress bar */}
        <div className="absolute bottom-0 left-0 h-[2px] w-full bg-gray-200/50 rounded-b-2xl overflow-hidden">
          <div
            className="h-full bg-orange-400 animate-[shrinkBar_linear_forwards]"
            style={{ animationDuration: `${duration}ms` }}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
};
